// @ts-check
// Sino de notificações no topo de cada página (contrato 08, seção 7): contagem de não lidas com polling, lista das
// mais recentes, marcar como lida ao abrir e "marcar todas". O Twenty não tem sino; o desenho segue os dropdowns dele.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { countUnreadNotifications, listNotifications, markNotificationsRead } from '../../data/automations.js';
import { navigate } from '../../app/router.js';
import { usePolling } from '../conversations/conversations-state.js';
import { Dropdown, DropdownMenuSeparator } from '../../ui/dropdown.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatDateTime, formatRelativeToNow } from '../../lib/format.js';
import { hasTranslation, t } from '../../i18n/index.js';

/** @typedef {import('../../data/automations.js').AppNotification} AppNotification */

const UNREAD_POLLING_INTERVAL_MILLISECONDS = 30_000;
const PANEL_PAGE_SIZE = 20;

export const NOTIFICATION_TYPE_ICONS = Object.freeze({
  TASK_ASSIGNED: 'IconCheckbox',
  TASK_DUE_SOON: 'IconClock',
  TASK_OVERDUE: 'IconAlertTriangle',
  LEAD_ASSIGNED: 'IconTargetArrow',
  MESSAGE_UNANSWERED: 'IconMessages',
  AUTOMATION: 'IconSettingsAutomation',
  MENTION: 'IconAt',
});

/** Não lidas do membro logado, compartilhado entre o sino e a página de preferências. */
export const unreadNotificationCountState = signal(0);

export async function refreshUnreadNotificationCount() {
  try {
    unreadNotificationCountState.value = await countUnreadNotifications();
  } catch {
    // Sem a rota (backend antigo) o sino fica sem contagem.
  }
}

/**
 * Título e texto no idioma da interface; avisos de automação usam o texto escrito pela escola.
 * @param {AppNotification} notification
 */
export function describeNotification(notification) {
  const params = notification.params ?? {};
  const prefix = `notifications.types.${notification.type}`;
  if (notification.type === 'AUTOMATION' || !hasTranslation(`${prefix}.title`)) return { title: notification.title, body: notification.body };
  // menção sem autor conhecido (escrita pela API): o título do servidor já diz o que houve
  if (notification.type === 'MENTION' && !params.actorName) return { title: notification.title, body: null };
  const variables = { ...params, due: params.dueAt ? formatDateTime(params.dueAt) : '' };
  const bodyKey = notification.type === 'TASK_ASSIGNED' || notification.type === 'LEAD_ASSIGNED'
    ? (params.actorName ? `${prefix}.bodyWithActor` : `${prefix}.body`)
    : `${prefix}.body`;
  const body = notification.type === 'MESSAGE_UNANSWERED' ? params.excerpt || null : hasTranslation(bodyKey) ? t(bodyKey, variables) : notification.body;
  return { title: t(`${prefix}.title`, variables), body };
}

/**
 * @param {{ notification: AppNotification, onOpen: (notification: AppNotification) => void }} props
 */
function NotificationItem({ notification, onOpen }) {
  const { title, body } = describeNotification(notification);
  return html`<button type="button" class="notification-item" data-unread=${notification.isRead ? undefined : 'true'} onClick=${() => onOpen(notification)}>
    <span class="notification-item-icon" data-type=${notification.type}>
      <${Icon} name=${NOTIFICATION_TYPE_ICONS[/** @type {keyof typeof NOTIFICATION_TYPE_ICONS} */ (notification.type)] ?? 'IconBell'} size=${16} />
    </span>
    <span class="notification-item-texts">
      <span class="notification-item-title">${title}</span>
      ${body ? html`<span class="notification-item-body">${body}</span>` : null}
      <span class="notification-item-time">${formatRelativeToNow(notification.createdAt)}</span>
    </span>
    ${notification.isRead ? null : html`<span class="notification-item-dot" aria-label=${t('notifications.unread')}></span>`}
  </button>`;
}

export function NotificationBell() {
  const anchorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState(/** @type {AppNotification[] | null} */ (null));
  const unreadCount = unreadNotificationCountState.value;

  useEffect(() => {
    refreshUnreadNotificationCount();
  }, []);
  usePolling(refreshUnreadNotificationCount, UNREAD_POLLING_INTERVAL_MILLISECONDS);

  const loadNotifications = async () => {
    try {
      const page = await listNotifications({ limit: PANEL_PAGE_SIZE });
      setNotifications(page.notifications);
      unreadNotificationCountState.value = page.unreadCount;
    } catch (error) {
      setNotifications([]);
      showErrorSnackbar(error);
    }
  };

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) loadNotifications();
  };

  /** @param {AppNotification} notification */
  const openNotification = async (notification) => {
    setIsOpen(false);
    if (!notification.isRead) {
      setNotifications((current) => current?.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item)) ?? null);
      markNotificationsRead({ ids: [notification.id] }).then((count) => {
        unreadNotificationCountState.value = count;
      }).catch(showErrorSnackbar);
    }
    if (notification.linkPath) navigate(notification.linkPath);
  };

  const markAllRead = async () => {
    try {
      unreadNotificationCountState.value = await markNotificationsRead({ all: true });
      setNotifications((current) => current?.map((item) => ({ ...item, isRead: true })) ?? null);
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const label = unreadCount > 0 ? t('notifications.bellWithCount', { count: unreadCount }) : t('notifications.title');
  return html`<div class="notification-bell" ref=${anchorRef}>
    <${LightIconButton} icon="IconBell" accent="secondary" size="medium" ariaLabel=${label} active=${isOpen} onClick=${toggle} data-testid="notification-bell" />
    ${unreadCount > 0 ? html`<span class="notification-bell-badge" aria-hidden="true">${unreadCount > 99 ? '99+' : unreadCount}</span>` : null}
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${360} maxHeight=${520} className="notification-panel">
      <div class="notification-panel-header">
        <span class="notification-panel-title">${t('notifications.title')}</span>
        ${unreadCount > 0
          ? html`<button type="button" class="notification-panel-link" onClick=${markAllRead}>${t('notifications.markAllRead')}</button>`
          : null}
      </div>
      <div class="notification-panel-list">
        ${notifications === null
          ? html`<div class="notification-panel-loading"><${Skeleton} height=${40} /><${Skeleton} height=${40} /></div>`
          : notifications.length === 0
            ? html`<div class="notification-panel-empty"><${Icon} name="IconBell" size=${24} /><span>${t('notifications.empty')}</span></div>`
            : notifications.map((notification) => html`<${NotificationItem} key=${notification.id} notification=${notification} onOpen=${openNotification} />`)}
      </div>
      <${DropdownMenuSeparator} />
      <button type="button" class="notification-panel-footer" onClick=${() => { setIsOpen(false); navigate('/settings/notifications'); }}>
        <${Icon} name="IconSettings" size=${14} />${t('notifications.preferencesLink')}
      </button>
    <//>
  </div>`;
}
